import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Boxes, Loader2, ArrowRight, DollarSign, ListOrdered, Bookmark, BookmarkCheck } from "lucide-react";
import { AIResourcesShell, StepLabel, DiffPill } from "../../components/ai/AIResourcesShell";
import { ResultPaywall } from "../../components/ai/ResultPaywall";
import { aiToolStack, aiSave, aiListSaved } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useAIResources } from "../../hooks/useAIResources";

const EXAMPLES = ["SaaS", "Restaurant", "Clothing Brand", "AI Startup", "Real Estate", "Marketing Agency"];

export default function AIToolStackAudit() {
  const { user, requireAuth } = useAuth();
  const { toolStack, patchToolStack } = useAIResources();
  const { building, input, result } = toolStack;
  const setBuilding = (v) => patchToolStack({ building: v });
  const setInput = (v) => patchToolStack({ input: v });
  const setResult = (v) => patchToolStack({ result: v });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);

  const loadSaved = useCallback(() => {
    if (!user) { setSaved([]); return; }
    aiListSaved("stack").then(setSaved).catch(() => setSaved([]));
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const run = async (what) => {
    const b = (what || input).trim();
    if (!b) { toast.error("Tell us what you're building"); return; }
    setBuilding(b); setLoading(true); setResult(null);
    try { setResult(await aiToolStack(b)); }
    catch { toast.error("Could not generate your stack. Try again."); }
    finally { setLoading(false); }
  };

  const save = () => {
    if (!result) return;
    requireAuth(async () => {
      try { await aiSave("stack", result); toast.success("Saved to My AI Stack"); loadSaved(); }
      catch { toast.error("Could not save"); }
    });
  };

  const sidebar = (
    <div className="rounded-2xl border border-gray-100 bg-white p-5">
      <p className="font-display font-bold text-[#0F0F0F]">My AI Stack</p>
      <p className="text-xs text-[#687280] mt-1">{saved.length} saved audit{saved.length !== 1 ? "s" : ""}.</p>
      <div className="mt-3 space-y-2">
        {saved.slice(0, 5).map((s) => (
          <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`saved-stack-${s.id}`}>
            <BookmarkCheck size={14} className="text-[#D4AF37] shrink-0" />
            <span className="text-[#0F0F0F] truncate">{s.data?.building}</span>
          </div>
        ))}
        {saved.length === 0 && <p className="text-xs text-[#9aa1ab]">Save an audit to build your stack.</p>}
      </div>
    </div>
  );

  return (
    <AIResourcesShell active="tool-stack" title="AI Tool Stack Audit" subtitle="Build your perfect AI toolkit. Stop paying for AI tools you don't need." icon={Boxes} extra={sidebar}>
      <section className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="stack-step1">
        <StepLabel n={1} />
        <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">What are you building?</h2>
        <p className="text-sm text-[#687280] mt-1">Blueprint AI will design the ideal AI tool stack for your mission.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {EXAMPLES.map((e) => (
            <button key={e} onClick={() => run(e)} className={`text-sm px-4 py-2 rounded-full border transition-colors ${building === e ? "border-[#D4AF37] bg-[#FBF7EA] text-[#8a6d1a] font-semibold" : "border-gray-200 text-[#374151] hover:border-[#D4AF37]"}`} data-testid={`stack-example-${e.toLowerCase().replace(/ /g, "-")}`}>{e}</button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Or describe what you're building..." className="flex-1 rounded-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="stack-input" />
          <button onClick={() => run()} disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="stack-run-btn">{loading ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />} Build My Stack</button>
        </div>
      </section>

      {loading && <div className="flex items-center justify-center py-16 text-[#687280]" data-testid="stack-loading"><Loader2 className="animate-spin mr-2" /> Blueprint AI is analyzing your mission…</div>}

      {result && (
        <ResultPaywall label="AI tool stack" testid="stack-paywall">
        <section className="mt-6" data-testid="stack-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <StepLabel n={2} />
              <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">Your AI Tool Stack</h2>
              <p className="text-sm text-[#687280] mt-1">Tailored for: <span className="font-semibold text-[#0F0F0F]">{result.building}</span></p>
            </div>
            <button onClick={save} className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#1c1c1c] transition-colors" data-testid="stack-save-btn"><Bookmark size={15} /> Save to My AI Stack</button>
          </div>

          <div className="mt-4 rounded-2xl bg-[#FBF7EA] border border-[#D4AF37]/30 p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-full bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center"><DollarSign size={22} /></span>
            <div><p className="text-[11px] font-bold uppercase tracking-wide text-[#8a6d1a]">Estimated Monthly Cost</p><p className="font-display text-2xl font-extrabold text-[#0F0F0F]">${result.total_monthly_cost} / month</p></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            {result.stack.map((s) => (
              <div key={s.category} className="rounded-2xl border border-gray-100 bg-white p-5" data-testid={`stack-item-${s.learning_order}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">{s.category}</p>
                    <h3 className="font-display text-lg font-bold text-[#0F0F0F]">{s.tool}</h3>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#687280] bg-[#F7F7F7] rounded-full px-2 py-1"><ListOrdered size={12} /> #{s.learning_order}</span>
                </div>
                <p className="text-sm text-[#687280] mt-1.5 leading-snug">{s.why}</p>
                <div className="flex items-center gap-4 mt-3 text-sm">
                  <span className="font-semibold text-[#0F0F0F]">{s.monthly_cost === 0 ? "Free" : `$${s.monthly_cost}/mo`}</span>
                  <DiffPill level={s.difficulty} />
                </div>
              </div>
            ))}
          </div>
        </section>
        </ResultPaywall>
      )}
    </AIResourcesShell>
  );
}
